import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider
} from '@mui/material';
import { CalendarToday, SwapHoriz } from '@mui/icons-material';
import NepaliDate from 'nepali-date-converter';

const DateConverter = () => {
  const today = new Date();
  const todayNepali = new NepaliDate(today);

  const [adDate, setAdDate] = useState(today.toISOString().split('T')[0]);
  const [bsDate, setBsDate] = useState({
    year: todayNepali.getYear(),
    month: todayNepali.getMonth() + 1,
    day: todayNepali.getDate()
  });

  const [convertedAD, setConvertedAD] = useState('');
  const [convertedBS, setConvertedBS] = useState('');

  const handleADtoBS = () => {
    try {
      const date = new Date(adDate);
      const nepaliDate = new NepaliDate(date);
      const formatted = nepaliDate.format('YYYY-MM-DD (dddd)');
      setConvertedBS(formatted);
    } catch (error) {
      setConvertedBS('Invalid date');
    }
  };

  const handleBStoAD = () => {
    try {
      const nepaliDate = new NepaliDate(
        parseInt(bsDate.year),
        parseInt(bsDate.month) - 1,
        parseInt(bsDate.day)
      );
      const adDateObj = nepaliDate.toJsDate();
      const formatted = adDateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      setConvertedAD(formatted);
    } catch (error) {
      setConvertedAD('Invalid date');
    }
  };

  const handleTodayAD = () => {
    const today = new Date();
    setAdDate(today.toISOString().split('T')[0]);
    const nepaliDate = new NepaliDate(today);
    setConvertedBS(nepaliDate.format('YYYY-MM-DD (dddd)'));
  };

  const handleTodayBS = () => {
    const today = new Date();
    const nepaliDate = new NepaliDate(today);
    setBsDate({
      year: nepaliDate.getYear(),
      month: nepaliDate.getMonth() + 1,
      day: nepaliDate.getDate()
    });
    const formatted = today.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    setConvertedAD(formatted);
  };

  return (
    <Box sx={{ mt: 8, px: 3, maxWidth: 1200, mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <CalendarToday sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Date Converter (AD ⇄ BS)
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Today's Date Display */}
        <Paper sx={{ p: 3, mb: 4, bgcolor: '#f5f5f5' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Today's Date
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                AD Date: {today.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                BS Date: {todayNepali.format('YYYY-MM-DD (dddd)')}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={4}>
          {/* AD to BS Converter */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, border: '2px solid #1976d2' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                AD to BS Converter
              </Typography>
              
              <TextField
                fullWidth
                type="date"
                label="AD Date"
                value={adDate}
                onChange={(e) => setAdDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SwapHoriz />}
                  onClick={handleADtoBS}
                >
                  Convert to BS
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleTodayAD}
                >
                  Today
                </Button>
              </Box>

              {convertedBS && (
                <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    BS Date:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {convertedBS}
                  </Typography>
                </Paper>
              )}
            </Paper>
          </Grid>

          {/* BS to AD Converter */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, border: '2px solid #2e7d32' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'success.main' }}>
                BS to AD Converter
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Year (YYYY)"
                    value={bsDate.year}
                    onChange={(e) => setBsDate({ ...bsDate, year: e.target.value })}
                    placeholder="2081"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Month (MM)"
                    value={bsDate.month}
                    onChange={(e) => setBsDate({ ...bsDate, month: e.target.value })}
                    placeholder="12"
                    inputProps={{ min: 1, max: 12 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Day (DD)"
                    value={bsDate.day}
                    onChange={(e) => setBsDate({ ...bsDate, day: e.target.value })}
                    placeholder="15"
                    inputProps={{ min: 1, max: 32 }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={<SwapHoriz />}
                  onClick={handleBStoAD}
                >
                  Convert to AD
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="success"
                  onClick={handleTodayBS}
                >
                  Today
                </Button>
              </Box>

              {convertedAD && (
                <Paper sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    AD Date:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {convertedAD}
                  </Typography>
                </Paper>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Info Section */}
        <Paper sx={{ p: 3, mt: 4, bgcolor: '#fff3e0' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> AD (Anno Domini) is the Gregorian calendar used internationally. 
            BS (Bikram Sambat) is the official calendar of Nepal. The Nepali calendar is approximately 
            56-57 years ahead of the Gregorian calendar.
          </Typography>
        </Paper>
      </Paper>
    </Box>
  );
};

export default DateConverter;
